import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, FormControl, InputLabel, Select, MenuItem, Modal, Typography } from "@mui/material";
import StatusChip from "../../components/StatusChip";
import { auditStatusTone, resilienceTone } from "../../status/StatusChipTones";
import { GraphQLClient } from "graphql-request";
import { formatEnum } from "../../utils/formatEnum";

import Header from "../../components/Header";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import { graphqlEndpoint } from "../../config";
import formOptions from "../../config/formOptions.json";

import { READ_OPERATIONAL_AUDITS } from "../../graph/operationalAudits/queries";
import { DELETE_OPERATIONAL_AUDIT } from "../../graph/operationalAudits/mutations";

const client = new GraphQLClient(graphqlEndpoint);


const ManageOperationalAudits = () => {
  const navigate = useNavigate();

  const [audits, setAudits] = useState([]);
  const [industryFilter, setIndustryFilter] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);

  const filteredAudits = industryFilter
    ? audits.filter((audit) => audit.industry === industryFilter)
    : audits;

  const fetchAudits = useCallback(async () => {
    try {
      const data = await client.request(READ_OPERATIONAL_AUDITS);

      setAudits(
        data.readOperationalAudits.map((audit) => ({
          id: audit.id,
          name: audit.name,
          industry: audit.industry,
          status: audit.status,
          totalQuestionsAnswered: audit.totalQuestionsAnswered || 0,
          totalProcessesIdentified: audit.totalProcessesIdentified || 0,
          documentedProcesses: audit.documentedProcesses || 0,
          singlePersonDependencies: audit.singlePersonDependencies || 0,
          unownedProcesses: audit.unownedProcesses || 0,
          operationalResilienceScore:
            audit.operationalResilienceScore === null ||
            audit.operationalResilienceScore === undefined
              ? 0
              : Number(audit.operationalResilienceScore),
          createdAt: audit.createdAt,
          completedAt: audit.completedAt || null,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch operational audits:", error);
    }
  }, []);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const handleEdit = (id) => {
    navigate(`/operationalAudits/edit/${id}`);
  };

  const handleRunAudit = (id) => {
    navigate(`/operationalAudits/run/${id}`);
  };

  const handleOpenDeleteModal = (audit) => {
    setSelectedAudit(audit);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedAudit(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAudit?.id) return;

    try {
      await client.request(DELETE_OPERATIONAL_AUDIT, {
        id: selectedAudit.id,
      });

      await fetchAudits();
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Failed to delete operational audit:", error);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Audit",
      flex: 1.4,
      cellClassName: "name-column--cell",
    },
    {
      field: "industry",
      headerName: "Industry",
      flex: 0.8,
      renderCell: ({ value }) => (
        <StatusChip label={value || "Unknown"} tone="default" />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ value }) => (
        <StatusChip label={formatEnum(value)} tone={auditStatusTone(value)} />
      ),
    },
    {
      field: "operationalResilienceScore",
      headerName: "Resilience",
      flex: 0.8,
      type: "number",
      renderCell: ({ value }) => (
        <StatusChip
          label={`${Math.round(value || 0)}%`}
          tone={resilienceTone(value)}
        />
      ),
    },
    {
      field: "totalQuestionsAnswered",
      headerName: "Answered",
      flex: 0.7,
      type: "number",
    },
    {
      field: "totalProcessesIdentified",
      headerName: "Processes",
      flex: 0.7,
      type: "number",
    },
    {
      field: "singlePersonDependencies",
      headerName: "Single Person",
      flex: 0.8,
      type: "number",
    },
    {
      field: "unownedProcesses",
      headerName: "Unowned",
      flex: 0.7,
      type: "number",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.4,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={1} alignItems="center">
          <ActionButton
            size="small"
            tone="gold"
            onClick={() => handleRunAudit(row.id)}
          >
            Run
          </ActionButton>

          <TableActions
            onEdit={() => handleEdit(row.id)}
            onDelete={() => handleOpenDeleteModal(row)}
            hideCreate
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="MANAGE OPERATIONAL AUDITS"
          subtitle="Create, run, and review operational health checks"
        />

        <ActionButton
          tone="gold"
          onClick={() => navigate("/createOperationalAudits")}
        >
          Create Audit
        </ActionButton>
      </Box>

      <Box display="flex" gap={2} alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Industry</InputLabel>
          <Select
            value={industryFilter}
            label="Industry"
            onChange={(e) => setIndustryFilter(e.target.value)}
          >
            <MenuItem value="">All Industries</MenuItem>
            {formOptions.industryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <ThemedDataGrid
        rows={filteredAudits}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
      />

      <Modal open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <Box
          p={4}
          bgcolor="background.paper"
          borderRadius="12px"
          mx="auto"
          my="20vh"
          width="420px"
          boxShadow={24}
        >
          <Typography variant="h6" mb={2}>
            Delete Operational Audit
          </Typography>

          <Typography mb={3}>
            Are you sure you want to delete{" "}
            <strong>{selectedAudit?.name || "this operational audit"}</strong>?
            This will also remove the responses captured for this audit.
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={handleCloseDeleteModal}>Cancel</Button>

            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ManageOperationalAudits;